/**
 * 设备详情/编辑对话框
 * v8.0 新增：IoT 设备管理功能
 */
import { useState, useEffect } from "react"
import { Loader2, CalendarPlus, Copy, Eye, EyeOff } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Checkbox } from "@/shared/components/ui/checkbox"
import { Badge } from "@/shared/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { DeviceApiService } from "@/modules/agent/services/device-api"
import { DEVICE_STATUS_CONFIG, getPermissionLabel, EXPIRES_OPTIONS } from "@/modules/agent/types/device"
import type { Device, DevicePermissions, DeviceUpdateRequest } from "@/modules/agent/types/device"

interface DeviceDetailDialogProps {
  projectId: string
  device: Device
  mode: 'view' | 'edit'
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

/**
 * 设备详情/编辑对话框
 */
export function DeviceDetailDialog({
  projectId,
  device,
  mode,
  open,
  onOpenChange,
  onSuccess,
}: DeviceDetailDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentMode, setCurrentMode] = useState(mode)

  // v8.2: 设备详情数据（包含 device_secret）
  const [deviceDetail, setDeviceDetail] = useState<Device | null>(null)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)

  // 编辑表单状态
  const [name, setName] = useState(device.name || "")
  const [permissions, setPermissions] = useState<DevicePermissions>(device.permissions)

  // 延长有效期状态
  const [showExtendForm, setShowExtendForm] = useState(false)
  const [extendDays, setExtendDays] = useState("365")
  
  // 密钥显示状态
  const [showSecret, setShowSecret] = useState(false)

  // v8.2: 打开对话框时调用详情 API 获取完整数据（包括 device_secret）
  useEffect(() => {
    if (open && device.device_id && projectId) {
      setIsLoadingDetail(true)
      DeviceApiService.get(projectId, device.device_id)
        .then(res => {
          if (res.data) {
            setDeviceDetail(res.data)
            // 更新表单状态
            setName(res.data.name || "")
            setPermissions(res.data.permissions)
          }
        })
        .catch(err => {
          console.error('获取设备详情失败:', err)
          // 失败时使用列表数据
          setDeviceDetail(null)
        })
        .finally(() => {
          setIsLoadingDetail(false)
        })
    }
  }, [open, device.device_id, projectId])

  // 当 device 或 mode 变化时重置状态
  useEffect(() => {
    if (!open) {
      // 关闭时重置
      setDeviceDetail(null)
      setShowExtendForm(false)
      setShowSecret(false)
      setError(null)
    }
    setCurrentMode(mode)
  }, [device, mode, open])

  // 使用详情数据或列表数据
  const displayDevice = deviceDetail || device

  /**
   * 格式化日期
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('zh-CN')
  }

  /**
   * 渲染状态徽章
   */
  const renderStatusBadge = () => {
    const config = DEVICE_STATUS_CONFIG[displayDevice.status]
    return (
      <Badge 
        variant={displayDevice.status === 'active' ? 'default' : displayDevice.status === 'blocked' ? 'destructive' : 'secondary'}
        className="gap-1"
      >
        <span>{config.icon}</span>
        {config.label}
      </Badge>
    )
  }

  /**
   * 保存编辑
   */
  const handleSave = async () => {
    if (!permissions.wss && !permissions.sse) {
      setError("请至少选择一项权限")
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const request: DeviceUpdateRequest = {
        name: name.trim() || undefined,
        permissions,
      }
      await DeviceApiService.update(projectId, device.device_id, request)
      onSuccess()
    } catch (err: unknown) {
      console.error("保存失败:", err)
      if (err instanceof Error) {
        setError(err.message || "保存失败，请重试")
      } else {
        setError("保存失败，请重试")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * 延长有效期
   */
  const handleExtend = async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      await DeviceApiService.extend(projectId, device.device_id, {
        extend_days: parseInt(extendDays),
      })
      setShowExtendForm(false)
      onSuccess()
    } catch (err: unknown) {
      console.error("延长有效期失败:", err)
      if (err instanceof Error) {
        setError(err.message || "延长有效期失败，请重试")
      } else {
        setError("延长有效期失败，请重试")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {currentMode === 'view' ? '设备详情' : '编辑设备'}
          </DialogTitle>
          <DialogDescription>
            设备 ID: {displayDevice.device_id}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* 基本信息 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">状态</Label>
              <div className="mt-1">{renderStatusBadge()}</div>
            </div>
            <div>
              <Label className="text-slate-500">权限</Label>
              <div className="mt-1 text-sm">{getPermissionLabel(displayDevice.permissions)}</div>
            </div>
          </div>

          {/* 设备密钥 */}
          <div>
            <Label className="text-slate-500">设备密钥</Label>
            {isLoadingDetail ? (
              <div className="mt-1 flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                加载中...
              </div>
            ) : displayDevice.device_secret ? (
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-xs bg-slate-100 px-2 py-1 rounded font-mono overflow-hidden">
                  {showSecret ? displayDevice.device_secret : '••••••••••••••••••••••••'}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setShowSecret(!showSecret)}
                  title={showSecret ? "隐藏密钥" : "显示密钥"}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    navigator.clipboard.writeText(displayDevice.device_secret || '')
                    // 简单提示
                    alert('已复制到剪贴板')
                  }}
                  title="复制密钥"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="mt-1 text-sm text-slate-400">无法获取密钥</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">订单号</Label>
              <div className="mt-1 text-sm font-mono">{displayDevice.order_no}</div>
            </div>
            <div>
              <Label className="text-slate-500">批次号</Label>
              <div className="mt-1 text-sm font-mono">{displayDevice.batch_no}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">创建时间</Label>
              <div className="mt-1 text-sm">{formatDate(displayDevice.created_at)}</div>
            </div>
            <div>
              <Label className="text-slate-500">激活时间</Label>
              <div className="mt-1 text-sm">{formatDate(displayDevice.activated_at)}</div>
            </div>
          </div>

          <div>
            <Label className="text-slate-500">有效期至</Label>
            <div className="mt-1 text-sm flex items-center gap-2">
              {formatDate(displayDevice.token_expires_at)}
              {currentMode === 'view' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setShowExtendForm(true)}
                >
                  <CalendarPlus className="h-3 w-3 mr-1" />
                  延长
                </Button>
              )}
            </div>
          </div>

          {/* 延长有效期表单 */}
          {showExtendForm && (
            <div className="bg-slate-50 p-4 rounded-lg space-y-3">
              <Label>延长有效期</Label>
              <div className="flex gap-2">
                <Select value={extendDays} onValueChange={setExtendDays}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="选择延长天数" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRES_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleExtend} disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  确认
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowExtendForm(false)}>
                  取消
                </Button>
              </div>
            </div>
          )}

          {/* 分隔线 */}
          <div className="border-t pt-4">
            {currentMode === 'view' ? (
              <>
                {/* 查看模式 - 只读显示 */}
                <div>
                  <Label className="text-slate-500">设备名称</Label>
                  <div className="mt-1 text-sm">{displayDevice.name || '-'}</div>
                </div>
              </>
            ) : (
              <>
                {/* 编辑模式 - 可编辑字段 */}
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">设备名称</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="可选，用于标识设备"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>访问权限</Label>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="wss"
                          checked={permissions.wss}
                          onCheckedChange={(checked) => 
                            setPermissions(prev => ({ ...prev, wss: !!checked }))
                          }
                        />
                        <label
                          htmlFor="wss"
                          className="text-sm font-medium leading-none"
                        >
                          语音通信 (WSS)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="sse"
                          checked={permissions.sse}
                          onCheckedChange={(checked) => 
                            setPermissions(prev => ({ ...prev, sse: !!checked }))
                          }
                        />
                        <label
                          htmlFor="sse"
                          className="text-sm font-medium leading-none"
                        >
                          文字通信 (SSE)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="server_vad"
                          checked={permissions.server_vad ?? false}
                          onCheckedChange={(checked) => 
                            setPermissions(prev => ({ ...prev, server_vad: !!checked }))
                          }
                        />
                        <label
                          htmlFor="server_vad"
                          className="text-sm font-medium leading-none"
                        >
                          云端 VAD <span className="text-amber-500 text-xs">(收费)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          {currentMode === 'view' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                关闭
              </Button>
              <Button onClick={() => setCurrentMode('edit')}>
                编辑
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setCurrentMode('view')}>
                取消
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                保存
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
